import React, { useEffect, useState } from "react";
import { Autocomplete, createFilterOptions } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getPlaces,
  setPlaceData,
  setSelectedPlace,
} from "store/slice/places/placeSlice";
import MDBadge from "components/MDBadge";
import { getPlaceById, setSinglePlace } from "store/slice/places/placeSlice";

const PlacePicker = ({ fromOnboardingDialog = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const placesData = useSelector((state) => state.places.placeData).filter(
    (item) => item.status === 10,
  );

  const user = useSelector((state) => state.users?.meInfo);
  const role = user?.roleTitle;

  const [selectedPlaceId, setSelectedPlaceId] = useState("");

  const selectedReduxPlace = useSelector((state) => state.places?.selectedPlace);

  // ✅ Keep local selectedPlaceId in sync with Redux selectedPlace.
  // This ensures if PlacePicker in the checklist dialog changes the place,
  // the PlacePicker in the module header also updates its value.
  useEffect(() => {
    if (selectedReduxPlace?._id && selectedReduxPlace._id !== selectedPlaceId) {
      setSelectedPlaceId(selectedReduxPlace._id);
    }
  }, [selectedReduxPlace?._id]);

  // ✅ Role-based keys
  const BRAND_ADMIN_KEYS = [
    "placeSettings",
    "pricingTierSettings",
    "ratesCreate",
    "ratesAssigned",
  ];

  const SUPER_ADMIN_KEYS = [
    "placeSettings",
    "pricingTierSettings",
    "generalSettings",
  ];

  const stepKeys =
    role === "Super Admin"
      ? SUPER_ADMIN_KEYS
      : role === "Brand Admin"
      ? BRAND_ADMIN_KEYS
      : [];

  // ✅ Correct completion logic
  const isPlaceCompleted = (option) => {
    if (!option || !stepKeys.length) return false;

    const steps = option?.setup?.steps || {};
    return stepKeys.every((key) => steps[key] === true);
  };

  useEffect(() => {
    handleGetPlaces();
  }, [placesData.length, selectedPlaceId]);

  const handleGetPlaces = async () => {
    try {
      if (placesData.length === 0) {
        const response = await dispatch(getPlaces()).unwrap();

        if (response?.success) {
          const transformedPlaces = response.places
            .filter((item) => item.status === 10)
            .map((item) => ({
              ...item,
              title: item.google.formatted_address,
            }));

          dispatch(setPlaceData(transformedPlaces));
          setDefaultPlaceId(transformedPlaces);
        }
      } else {
        setDefaultPlaceId(placesData);
      }
    } catch (error) {
      console.error("Error getting places:", error);
    }
  };

  const setDefaultPlaceId = (data) => {
    // ✅ Priority 1: a newly created place (from sessionStorage)
    const newPlaceId = sessionStorage.getItem("newlyCreatedPlaceId");
    if (newPlaceId) {
      sessionStorage.removeItem("newlyCreatedPlaceId"); // consume flag
      localStorage.setItem("placeId", newPlaceId);
      setSelectedPlaceId(newPlaceId);
      dispatch(getPlaceById(newPlaceId))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(setSinglePlace(res.place));
            dispatch(setSelectedPlace(res.place));
          }
        });
      return;
    }

    // ✅ Priority 2: remembered place from localStorage
    const storedPlaceId = localStorage.getItem("placeId");

    if (storedPlaceId) {
      setSelectedPlaceId(storedPlaceId);

      dispatch(getPlaceById(storedPlaceId))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(setSinglePlace(res?.place));
            dispatch(setSelectedPlace(res?.place));
          }
        });
    } else if (placesData.length > 0) {
      let placeId = new URLSearchParams(location?.search).get("placeId");
      let place = placesData.find((item) => item._id === placeId);

      if (placeId && place) {
        setSelectedPlaceId(placeId);
        dispatch(setSelectedPlace(place));
        dispatch(placeId)
          .unwrap()
          .then((res) => {
            if (res?.success) {
              dispatch(setSinglePlace(res?.place));
              dispatch(setSelectedPlace(res?.place));
            }
          });
      } else {
        setSelectedPlaceId(placesData[0]._id);
        dispatch(getPlaceById(placesData[0]._id))
          .unwrap()
          .then((res) => {
            if (res?.success) {
              dispatch(setSinglePlace(res?.place));
              dispatch(setSelectedPlace(res?.place));
            }
          });
      }
    }
  };

  const handleOptionChange = (_, selectedOption) => {
    if (selectedOption) {
      localStorage.setItem("placeId", selectedOption._id);
      setSelectedPlaceId(selectedOption._id);
      dispatch(setSelectedPlace(selectedOption));
      dispatch(getPlaceById(selectedOption._id))
        .unwrap()
        .then((res) => {
          console.log("res ===>", res);
          if (res?.success) {
            console.log("YOYO, Response", res?.place);
            dispatch(setSinglePlace(res?.place));
            dispatch(setSelectedPlace(res?.place));
          }
        });
    }
  };

  const filterOptions = createFilterOptions({
    stringify: (option) =>
      [
        option?.parkingCode,
        option?.google?.formatted_address,
        option?.timeZoneId,
      ]
        .filter(Boolean)
        .join(" "),
  });

  return (
    <MDBox height="100%" mt={0.5} lineHeight={1}>
      <Autocomplete
        options={placesData}
        getOptionLabel={(option) =>
          `( ${option.parkingCode} ) - ${option?.google?.formatted_address} (${option.timeZoneId})`
        }
        renderOption={(props, option) => {
          const isCompleted = isPlaceCompleted(option);
          const isSkipped = option?.setup?.skipped?.isSkipped;
          const isPermanent = option?.setup?.skipped?.isPermanent;

          return (
            <li {...props}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Parking Code */}
                <MDBadge
                  badgeContent={<p>{option.parkingCode}</p>}
                  color="secondary"
                  variant="gradient"
                  size="md"
                />

                {/* Address */}
                <span>{option?.google?.formatted_address}</span>

                {/* ✅ PRIORITY: Completed > Permanent > Skipped */}
                {fromOnboardingDialog && isCompleted && (
                  <MDBadge
                    badgeContent="Completed"
                    color="success"
                    size="xs"
                    variant="contained"
                  />
                )}

                {fromOnboardingDialog && !isCompleted && isPermanent && (
                  <MDBadge
                    badgeContent="Skipped Permanently"
                    color="error"
                    size="xs"
                    variant="contained"
                  />
                )}

                {fromOnboardingDialog &&
                  !isCompleted &&
                  !isPermanent &&
                  isSkipped && (
                    <MDBadge
                      badgeContent="Skipped"
                      color="warning"
                      size="xs"
                      variant="contained"
                    />
                  )}
              </div>
            </li>
          );
        }}
        value={
          placesData.find((option) => option._id === selectedPlaceId) || null
        }
        onChange={handleOptionChange}
        disableClearable
        size="small"
        renderInput={(params) => {
          const selected = placesData.find(
            (option) => option._id === selectedPlaceId
          );

          const isCompleted = isPlaceCompleted(selected);
          const isSkipped = selected?.setup?.skipped?.isSkipped;
          const isPermanent = selected?.setup?.skipped?.isPermanent;

          return (
            <MDInput
              {...params}
              label="Place"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {/* ✅ PRIORITY DISPLAY */}
                    {fromOnboardingDialog && isCompleted && (
                      <span style={{ color: "green", fontSize: 11 }}>
                        (Completed)
                      </span>
                    )}

                    {fromOnboardingDialog && !isCompleted && isPermanent && (
                      <span style={{ color: "red", fontSize: 11 }}>
                        (Permanent Skip)
                      </span>
                    )}

                    {fromOnboardingDialog &&
                      !isCompleted &&
                      !isPermanent &&
                      isSkipped && (
                        <span style={{ color: "orange", fontSize: 11 }}>
                          (Skipped)
                        </span>
                      )}

                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
    </MDBox>
  );
};

export default PlacePicker;